import * as XLSX from 'xlsx';
import { ISubscriptionResourceEnum } from '../../repository/jago/model/input/ISubscriptionResourceEnum';
import { SubscriptionLogicEnum } from '../../repository/jago/model/input/ISubscriptionLogicEnum';
import { SubscriptionProvisioningTypeEnum } from '../../repository/jago/model/input/ISubscriptionProvisioningTypeEnum';
import { activationBaselineSubscriptions } from '../activationBaselineSubscriptions';
import { IPricelistParseError, IPricelistParseResult, IPricelistRow } from './types';

export const PRICELIST_HEADERS = {
  company: 'COMPANY (MARKETPLACE)',
  sku: 'SKU',
  description: 'DESCRIPTION',
  resource: 'RESOURCE',
  payment: 'PAYMENT',
  logic: 'LOGIC',
  maxElements: 'MAX ELEMENTS',
  maxUsers: 'MAX USERS',
  maxLocks: 'MAX LOCKS',
  price: 'PRICE',
  visible: 'VISIBLE',
  burnRatePeriod: 'BURN RATE PERIOD',
  category: 'CATEGORY',
  mainTierSubscriptionTypes: 'MAIN TIER SUBSCRIPTION TYPES',
  fixedPrice: 'FIXED PRICE',
  quotationRequired: 'QUOTATION REQUIRED',
  provisioning: 'PROVISIONING',
  canBeRemoved: 'CAN BE REMOVED',
} as const;

type HeaderKey = keyof typeof PRICELIST_HEADERS;

/** Column order of the template / legacy sheet. */
export const PRICELIST_TEMPLATE_HEADERS: string[] = Object.keys(PRICELIST_HEADERS).map(k => PRICELIST_HEADERS[k as HeaderKey]);

/** Alternative header names accepted for a column (the STAGING sheet calls PRICE "PRICE (credits)"). */
const HEADER_ALIASES: Partial<Record<HeaderKey, string[]>> = {
  price: ['PRICE (credits)'],
};

const OPTIONAL_HEADERS: HeaderKey[] = ['category', 'provisioning'];

export const RECURRENT_PAYMENT = 'RECURRENT';

type Cell = string | number | boolean | Date | null | undefined;

/** Lowercase and strip non-alphanumerics so `mainTierSubscriptionTypes` matches `MAIN TIER SUBSCRIPTION TYPES`. */
const normalizeHeader = (h: Cell): string => String(h === null || h === undefined ? '' : h).toLowerCase().replace(/[^a-z0-9]/g, '');

const cellToString = (c: Cell): string => (c === null || c === undefined ? '' : String(c).trim());

/** Names of the sheets of a workbook that look like a price list ("LISTINO"). */
export function findPricelistSheetNames(workbook: XLSX.WorkBook): string[] {
  return workbook.SheetNames.filter(name => name.toLowerCase().includes('listino'));
}

export function readSheetRows(workbook: XLSX.WorkBook, sheetName: string): Cell[][] {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    throw new Error(`Sheet "${sheetName}" not found`);
  }
  return XLSX.utils.sheet_to_json<Cell[]>(sheet, { header: 1, raw: true, defval: '', blankrows: true });
}

/** Maps every known header to its column index. Throws listing the missing required headers. */
export function locateColumns(headerRow: Cell[]): Record<HeaderKey, number> {
  const normalized = headerRow.map(normalizeHeader);
  const columns = {} as Record<HeaderKey, number>;
  const missing: string[] = [];
  (Object.keys(PRICELIST_HEADERS) as HeaderKey[]).forEach(key => {
    const names = [PRICELIST_HEADERS[key], ...(HEADER_ALIASES[key] || [])].map(normalizeHeader);
    const index = normalized.findIndex(h => names.includes(h));
    columns[key] = index;
    if (index < 0 && !OPTIONAL_HEADERS.includes(key)) {
      missing.push(PRICELIST_HEADERS[key]);
    }
  });
  if (missing.length > 0) {
    throw new Error(`Missing required columns: ${missing.join(', ')}`);
  }
  return columns;
}

const KNOWN_MAIN_TIER_TYPES: string[] = activationBaselineSubscriptions.map(t => t.name);

/**
 * Parses the "MAIN TIER SUBSCRIPTION TYPES" cell (comma, semicolon or space separated, e.g. `ENTERPRISE, BASIC`).
 * Unknown types are returned apart so the caller can warn about them.
 */
export function parseMainTierTypes(raw: Cell): { types: string[]; unknown: string[] } {
  const types: string[] = [];
  const unknown: string[] = [];
  cellToString(raw)
    .split(/[,;\s]+/)
    .map(t => t.trim().toUpperCase())
    .filter(t => t.length > 0)
    .forEach(t => {
      const target = KNOWN_MAIN_TIER_TYPES.includes(t) ? types : unknown;
      if (!target.includes(t)) target.push(t);
    });
  return { types, unknown };
}

/** Legacy script mapping: recurrent payments are spread over the burn rate period (in days). */
export function computeBurnRate(price: number, payment: string, periodDays: number): number {
  if (payment !== RECURRENT_PAYMENT) {
    return 0;
  }
  return Math.floor(price * 100 / periodDays);
}

/** Legacy script mapping `(FIXED PRICE || 0) * 100`, rounded to avoid float artifacts (2504.7 * 100). */
export function computeFixedPrice(fixedPrice: number): number {
  return Math.round((fixedPrice || 0) * 100);
}

const isEmptyRow = (row: Cell[]): boolean => row.every(c => cellToString(c) === '');

const parseNumber = (raw: Cell, label: string, errors: string[]): number => {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw < 0) {
      errors.push(`${label} must be a non-negative number`);
      return 0;
    }
    return raw;
  }
  const text = cellToString(raw);
  if (!text) {
    return 0;
  }
  const value = Number(text.replace(',', '.'));
  if (!Number.isFinite(value) || value < 0) {
    errors.push(`${label} must be a non-negative number (got "${text}")`);
    return 0;
  }
  return value;
};

const parseFlag = (raw: Cell, label: string, errors: string[]): boolean => {
  if (typeof raw === 'boolean') {
    return raw;
  }
  if (typeof raw === 'number') {
    if (raw === 0 || raw === 1) {
      return raw === 1;
    }
  } else {
    const text = cellToString(raw).toLowerCase();
    if (['', '0', 'false', 'no', 'n'].includes(text)) {
      return false;
    }
    if (['1', 'true', 'yes', 'y', 'si', 'sì'].includes(text)) {
      return true;
    }
  }
  errors.push(`${label} must be 0 or 1 (got "${cellToString(raw)}")`);
  return false;
};

const KNOWN_RESOURCES: string[] = Object.keys(ISubscriptionResourceEnum);

/** Parses one sheet. Throws only for structural problems (sheet / headers); row problems are returned in `errors`. */
export function parsePricelist(workbook: XLSX.WorkBook, sheetName: string): IPricelistParseResult {
  const allRows = readSheetRows(workbook, sheetName);
  if (allRows.length === 0) {
    throw new Error(`Sheet "${sheetName}" is empty`);
  }
  const columns = locateColumns(allRows[0]);
  const rows: IPricelistRow[] = [];
  const parseErrors: IPricelistParseError[] = [];
  const at = (row: Cell[], key: HeaderKey): Cell => (columns[key] >= 0 ? row[columns[key]] : undefined);

  allRows.slice(1).forEach((cells, index) => {
    if (isEmptyRow(cells)) {
      return;
    }
    const rowNumber = index + 2;
    const errors: string[] = [];
    const warnings: string[] = [];
    const company = cellToString(at(cells, 'company'));
    const sku = cellToString(at(cells, 'sku'));
    const name = cellToString(at(cells, 'description'));
    const resource = cellToString(at(cells, 'resource'));
    const payment = cellToString(at(cells, 'payment')).toUpperCase();
    const logicText = cellToString(at(cells, 'logic')).toUpperCase();
    const provisioningText = cellToString(at(cells, 'provisioning')).toUpperCase();
    const category = cellToString(at(cells, 'category'));

    if (!sku) errors.push('SKU is required');
    if (!name) errors.push('DESCRIPTION is required');
    if (!resource) errors.push('RESOURCE is required');
    else if (!KNOWN_RESOURCES.includes(resource)) warnings.push(`Unknown resource "${resource}"`);

    let logic = SubscriptionLogicEnum.FIXED;
    if (!(Object.values(SubscriptionLogicEnum) as string[]).includes(logicText)) {
      errors.push(`Invalid LOGIC "${logicText}"`);
    } else {
      logic = logicText as SubscriptionLogicEnum;
    }

    let provisioningType: SubscriptionProvisioningTypeEnum | undefined;
    if (provisioningText) {
      if (!(Object.values(SubscriptionProvisioningTypeEnum) as string[]).includes(provisioningText)) {
        errors.push(`Invalid PROVISIONING "${provisioningText}"`);
      } else {
        provisioningType = provisioningText as SubscriptionProvisioningTypeEnum;
      }
    }

    if (payment && payment !== RECURRENT_PAYMENT) {
      warnings.push(`PAYMENT "${payment}" is not RECURRENT: burn rate set to 0`);
    }

    const price = parseNumber(at(cells, 'price'), 'PRICE', errors);
    const period = parseNumber(at(cells, 'burnRatePeriod'), 'BURN RATE PERIOD', errors);
    const fixedPriceRaw = parseNumber(at(cells, 'fixedPrice'), 'FIXED PRICE', errors);
    const maxElements = parseNumber(at(cells, 'maxElements'), 'MAX ELEMENTS', errors);
    const maxUsers = parseNumber(at(cells, 'maxUsers'), 'MAX USERS', errors);
    const maxLocks = parseNumber(at(cells, 'maxLocks'), 'MAX LOCKS', errors);
    if (payment === RECURRENT_PAYMENT && !(period > 0)) {
      errors.push('BURN RATE PERIOD is required for RECURRENT payments');
    }

    const visible = parseFlag(at(cells, 'visible'), 'VISIBLE', errors);
    const requestToQuote = parseFlag(at(cells, 'quotationRequired'), 'QUOTATION REQUIRED', errors);
    const canBeDeactivated = parseFlag(at(cells, 'canBeRemoved'), 'CAN BE REMOVED', errors);
    const mainTierTypes = parseMainTierTypes(at(cells, 'mainTierSubscriptionTypes'));
    mainTierTypes.unknown.forEach(t => warnings.push(`Unknown main tier subscription type "${t}" ignored`));

    if (errors.length > 0) {
      parseErrors.push({ rowNumber, company, sku, messages: errors });
      return;
    }
    rows.push({
      rowNumber,
      company,
      sku,
      name,
      resource,
      payment,
      logic,
      maxElements,
      maxUsers,
      maxLocks,
      visible,
      mainTierTypes: mainTierTypes.types,
      category: category || undefined,
      fixedPrice: computeFixedPrice(fixedPriceRaw),
      burnRate: computeBurnRate(price, payment, period),
      requestToQuote,
      provisioningType,
      canBeDeactivated,
      warnings,
    });
  });
  return { rows, errors: parseErrors };
}

/** Distinct COMPANY values (in order of appearance) among parsed rows and rows in error. */
export function distinctCompanies(result: IPricelistParseResult): string[] {
  const companies: string[] = [];
  [...result.rows, ...result.errors].forEach(r => {
    if (r.company && !companies.includes(r.company)) {
      companies.push(r.company);
    }
  });
  return companies;
}

/** Default COMPANY value for a marketplace: the one equal (case-insensitive) to its name. */
export function defaultCompanyFor(companies: string[], marketplaceName?: string): string | undefined {
  if (!marketplaceName) {
    return undefined;
  }
  const target = marketplaceName.trim().toLowerCase();
  return companies.find(c => c.trim().toLowerCase() === target);
}

/** Builds an empty workbook with the expected headers. */
export function buildTemplateWorkbook(): XLSX.WorkBook {
  const sheet = XLSX.utils.aoa_to_sheet([PRICELIST_TEMPLATE_HEADERS]);
  return { Sheets: { 'PROD - LISTINO': sheet }, SheetNames: ['PROD - LISTINO'] };
}
